/**
 * How `getUniversalAggregation` should issue a set of requested aggregations (ATL-1101).
 *
 * ## Why this is its own module
 *
 * Firestore exposes two entry points — `getCountFromServer` (count only, cheapest) and
 * `getAggregateFromServer` (an arbitrary spec) — and `getUniversalAggregation` has to choose
 * between them TWICE, once in its web branch and once in its React-Native branch. When those two
 * branches each carried their own copy of the choice, they were free to drift, and neither can be
 * exercised from a unit test: one needs a browser Firestore, the other a device.
 *
 * So the decision lives here, as a pure function over the request, and
 * `aggregationPlan.test.js` pins it. What each branch then does is mechanical.
 *
 * ## The decision, and the defect it exists to prevent
 *
 * A count requested ALONGSIDE sums or averages must go in the SAME `getAggregateFromServer` spec.
 * Issuing it separately means two server round trips and therefore two different snapshots: the
 * daily-stats sweep runs against TODAY on an actively selling till, so a sale committing between
 * the calls makes `count` and `sum(finalTotalCartPrice)` disagree *by construction*. That reads as
 * drift, fires a Sentry warning, and triggers a recompute whose absolute `set()` then races the
 * concurrent `fieldValue.increment` writes it is trying to correct.
 *
 * A count on its own still uses `getCountFromServer` — it is the cheaper call, and the helper's
 * other two callers ask for nothing else.
 */

export interface AggregationRequest {
  type: 'count' | 'sum' | 'average'
  alias: string
  field?: string
}

export interface AggregationPlan {
  /** Issue a standalone `getCountFromServer` for this alias. */
  standaloneCountAlias: string | null
  /** Include a `count()` under this alias in the aggregate spec. */
  countInSpecAlias: string | null
  sums: AggregationRequest[]
  averages: AggregationRequest[]
  /** Whether an aggregate query is needed at all. */
  needsAggregateQuery: boolean
}

export function planAggregations(aggregations: readonly AggregationRequest[]): AggregationPlan {
  const countAgg = aggregations.find((agg) => agg.type === 'count') ?? null
  // A sum/average with no `field` cannot be expressed and is dropped by both branches; excluding it
  // here keeps the plan honest about whether an aggregate query is actually needed — otherwise a
  // request of `[count, {type:'sum'}]` would move the count into an empty spec and lose it.
  const sums = aggregations.filter((agg) => agg.type === 'sum' && Boolean(agg.field))
  const averages = aggregations.filter((agg) => agg.type === 'average' && Boolean(agg.field))
  const needsAggregateQuery = sums.length > 0 || averages.length > 0

  return {
    standaloneCountAlias: countAgg && !needsAggregateQuery ? countAgg.alias : null,
    countInSpecAlias: countAgg && needsAggregateQuery ? countAgg.alias : null,
    sums,
    averages,
    needsAggregateQuery,
  }
}
